import { useEffect } from 'react';
import {
  Sparkles, Share2, FileSpreadsheet, ScrollText, Calculator,
  PieChart, Calendar, GitCompare, Receipt, BarChart3,
  Download, Upload, Droplets, ArrowRightLeft, ShieldCheck,
} from 'lucide-react';
import ModalCloseButton from './ModalCloseButton';

/**
 * ToolBoxModal — single launcher modal for every portfolio-page tool.
 *
 * Lives only on the Portfolio page. Renders a 4-column (mobile) /
 * 5-column (desktop) grid of tool tiles ordered alphabetically by
 * label, top-left → right, then next row. Clicking any tile (icon
 * OR text) closes the modal and fires the corresponding action via
 * `onAction(key)`. Also closes on:
 *   • X button (top-right, standard ModalCloseButton)
 *   • Tap on the dark overlay (any click outside the white card)
 *
 * The toolbox PNG asset (`/icons/toolbox.png`) is shown at the top of
 * the modal as the visual hero. Falls back to a Lucide wrench if the
 * image fails to load.
 *
 * NOTE: Some tools (e.g. share-page, google-sheet) require the
 * portfolio to be non-empty. The parent's `onAction` honour the same
 * empty-portfolio guard the original icons use, so we don't duplicate
 * that logic here.
 */
const TOOLS = [
  { key: 'account-audit',    label: 'Account Audit',    icon: ShieldCheck,   color: 'text-rose-500' },
  { key: 'all-transactions', label: 'All Transactions', icon: ScrollText,    color: 'text-violet-500' },
  { key: 'basic-calculator', label: 'Basic Calculator', icon: Calculator,    color: 'text-fuchsia-700 dark:text-amber-200' },
  { key: 'calendar',         label: 'Calendar',         icon: Calendar,      color: 'text-sky-500' },
  { key: 'compare',          label: 'Compare',          icon: GitCompare,    color: 'text-indigo-500' },
  { key: 'compare-theses',   label: 'Compare Theses',   icon: ArrowRightLeft, color: 'text-violet-500' },
  { key: 'drip-calculator',  label: 'DRIP Calculator',  icon: Droplets,      color: 'text-emerald-500' },
  { key: 'export-csv',       label: 'Export CSV',       icon: Download,      color: 'text-amber-500' },
  { key: 'google-sheet',     label: 'Google / Excel Sheet', icon: FileSpreadsheet, color: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'import-csv',       label: 'Import CSV',       icon: Upload,        color: 'text-emerald-500' },
  { key: 'my-share-page',    label: 'My Share Page',    icon: Share2,        color: 'text-blue-500' },
  { key: 'rebalancer',       label: 'Rebalancer',       icon: PieChart,      color: 'text-fuchsia-500' },
  { key: 'tax-lots',         label: 'Tax Lots',         icon: Receipt,       color: 'text-cyan-500' },
  { key: 'what-if',          label: 'What If?',         icon: Sparkles,      color: 'text-violet-500' },
  { key: 'year-end',         label: 'Year-End',         icon: BarChart3,     color: 'text-amber-500' },
];

export default function ToolBoxModal({ open, onClose, onAction }) {
  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const handleClickTool = (key) => {
    onClose();
    // Defer the action one tick so the modal teardown doesn't fight
    // with the launched modal's mount transition.
    setTimeout(() => onAction?.(key), 30);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="toolbox-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Tool Box"
    >
      <div
        // Click inside the card MUST NOT close the modal (only icons or X do).
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto bg-background rounded-2xl border shadow-2xl pb-24 sm:pb-4"
        data-testid="toolbox-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b bg-muted/40 sticky top-0 z-10">
          <div className="flex items-center gap-2.5">
            <img
              src="/icons/toolbox.png"
              alt=""
              className="w-8 h-8 object-contain shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div>
              <h3 className="text-base font-bold leading-tight">Tool Box</h3>
              <p className="text-[11px] text-muted-foreground leading-tight">Every portfolio tool in one place</p>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} testId="toolbox-modal-close" />
        </div>

        {/* Grid of tools — alphabetical L→R, top→bottom */}
        <div
          className="p-4 grid grid-cols-3 sm:grid-cols-4 gap-3"
          data-testid="toolbox-modal-grid"
        >
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => handleClickTool(t.key)}
                data-testid={`toolbox-btn-${t.key}`}
                className="group flex flex-col items-center justify-start gap-2 p-2.5 rounded-xl border bg-card hover:bg-muted/60 hover:border-fuchsia-500/40 dark:hover:border-amber-200/40 transition-all text-center active:scale-95"
              >
                <span className="w-10 h-10 rounded-full bg-muted/60 group-hover:bg-background border border-border/60 flex items-center justify-center transition-colors">
                  <Icon className={`w-5 h-5 ${t.color}`} />
                </span>
                <span className="text-[11px] font-semibold leading-tight text-foreground/90 break-words">
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Footer hint */}
        <div className="px-4 pb-3 text-[10px] text-muted-foreground/70 text-center">
          Tap a tool to open it. Tap outside or × to close.
        </div>
      </div>
    </div>
  );
}
